import React, { useContext } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { Tooltip } from 'react-tooltip';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { useStateSelector } from 'src/redux';
import { formatNumber } from 'src/utils/misc';
import { Variable } from 'src/api/types';
import { VariablePill, noDataTooltipHtml } from 'src/common/VariablePill';
import { VariablesContext } from '../context';
import { getVariableIcon } from './variableIcon';

// Editable formula chip (Tiptap node view). Shares the pill chrome + red "unpopulated"
// treatment with the read-only VariableCapsule via VariablePill; the extras here are
// Tiptap-specific (inline flow alignment, space preservation, an icon baseline nudge,
// and the tighter mobile padding).
const ChipPill = styled(VariablePill)`
  margin: 0 2px;
  vertical-align: middle;

  * {
    white-space: pre;
  }

  /* Centre the chip on the line (not the baseline) so a larger/smaller icon never
     shifts it relative to its neighbours or the surrounding operators. */
  .vp-icon {
    transform: translateY(-1px);
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 0 8px;
  }
`;

export const VariableChip: React.FC<NodeViewProps> = ({ node }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const { variables, workspaceVariableNames } = useContext(VariablesContext);

  const id = Number(node.attrs.id);
  const ctxVar = variables.find((v) => v.id === id) as Variable | undefined;
  const label = ctxVar?.alias || ctxVar?.name || node.attrs.label || `#${node.attrs.id}`;
  const tooltipId = `fi-chip-${node.attrs.id}`;

  // Live context value is the source of truth; fall back to the value carried on the node
  // (set at insert time after a "+ Add Value" save) to avoid a no-data flicker pre-refetch.
  const data: Variable | undefined = ctxVar
    ? _.isNil(ctxVar.value) && !_.isNil(node.attrs.value)
      ? { ...ctxVar, value: node.attrs.value, currency: node.attrs.currency ?? ctxVar.currency }
      : ctxVar
    : undefined;

  const unpopulated = _.isNil(data?.value);
  const tooltipValue = unpopulated
    ? noDataTooltipHtml(colors.statusRed)
    : formatNumber(data?.value || 0, { symbol: !!data?.currency, maxDecimals: 3 });

  return (
    <NodeViewWrapper as="span" contentEditable={false} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <Tooltip
        id={tooltipId}
        style={{ backgroundColor: colors.prussianBlue, zIndex: 10, color: colors.white }}
        opacity={1}
      />
      <ChipPill
        tag="span"
        radius={10}
        padding="0 10px"
        gap={5}
        unpopulated={unpopulated}
        icon={unpopulated || !data ? undefined : getVariableIcon(data, workspaceVariableNames)}
        data-tooltip-id={tooltipId}
        data-tooltip-html={tooltipValue}
      >
        {label}
      </ChipPill>
    </NodeViewWrapper>
  );
};
